import React, { useEffect } from 'react';
import { IconButton, TextField, Card, Typography, Grid, Autocomplete, Checkbox, FormHelperText, Chip, Box, InputAdornment } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from 'sweetalert2';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

const formatValue = (value) => {
    if (typeof value !== 'string') {
      return value === 0 ? '0' : value?.toString() || '';
    }
    const formatted = value.replace(/^0+(?=\d)/, '').replace(/^\./, '0.');
    return formatted === '' ? '' : formatted;
};

const SelectedServices = ({ providerServiceList, serviceList }) => {
  const { watch, setValue, formState: { errors }, register, trigger, setError, clearErrors } = useFormContext();
  const services = watch('services') || [];

  // Get brand list with default fallback
  // const getBrandList = (selectedService) => {
  //   const brandList = serviceList.find(serv => serv.masterServiceId === selectedService.servicesMasterId)?.brands;
  //   if (brandList && brandList.length > 0) {
  //     return brandList;
  //   } else {
  //     return [
  //       {
  //         "brandId": 1,
  //         "brandDetail": {
  //           "title": "General Brand",
  //         }
  //       } 
  //     ];
  //   }
  // };
  // Updated getBrandList function for SelectedServices component
const getBrandList = (selectedService) => {
  // Get the full brand list for this service
  const fullBrandList = serviceList.find(serv => serv.masterServiceId === selectedService.servicesMasterId)?.brands || [];
  
  if (fullBrandList.length > 0) {
    // Get existing brands for this specific child service from providerServiceList
    const existingServices = providerServiceList?.filter(service => 
      service.servicesChildId === selectedService.servicesChildId
    ) || [];
    
    if (existingServices.length === 0) {
      // No existing services for this child service, exclude only default brand (ID 1)
      return fullBrandList.filter(brand => brand.brandId !== 1);
    }

    // Get existing brand IDs from providerServiceList
    const existingBrandIds = existingServices.map(service => service.brand?.id).filter(Boolean);
    
    console.log('Child Service ID:', selectedService.servicesChildId);
    console.log('Existing Services:', existingServices);
    console.log('Existing Brand IDs:', existingBrandIds);
    console.log('Full Brand List:', fullBrandList);
    
    // Filter out existing brands and default brand
    const availableBrands = fullBrandList.filter(brand => 
      brand.brandId !== 1 && // Exclude default brand
      !existingBrandIds.includes(brand.brandId) // Exclude already used brands
    );
    
    console.log('Available Brands:', availableBrands);

    // If no brands are available, return default brand
    if (availableBrands.length === 0) {
      return [
        {
          "brandId": 1,
          "brandDetail": {
            "title": "General Brand",
          }
        } 
      ];
    }

    return availableBrands;
  } else {
    // No brands found in service list, return default
    return [
      {
        "brandId": 1,
        "brandDetail": {
          "title": "General Brand",
        }
      } 
    ];
  }
};

  // Auto-select default brand when no brands available
  const autoSelectDefaultBrand = async (serviceIndex, selectedService) => {
    const availableBrands = getBrandList(selectedService);
    
    // If only default brand is available and no brands are selected yet
    if (availableBrands.length === 1 && availableBrands[0].brandDetail.title === "General Brand") {
      const updatedServices = [...services];
      
      if (updatedServices[serviceIndex] && (!updatedServices[serviceIndex].ServiceBrands || updatedServices[serviceIndex].ServiceBrands.length === 0)) {
        const serviceBrands = [{
          serviceBrandId: availableBrands[0].brandId,
          serviceBrandName: availableBrands[0].brandDetail.title,
          serviceTime: 0,
          price: 0
        }];
        
        updatedServices[serviceIndex] = {
          ...updatedServices[serviceIndex],
          ServiceBrands: serviceBrands
        };
        
        setValue('services', updatedServices, { shouldDirty: true });
        await trigger('services');
      }
    }
  };

  const handleRemoveService = async (servicesMasterId, servicesChildId) => {
    const result = await Swal.fire({
      text: "Do you want to Delete Selected Service?",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Delete!',
      cancelButtonText: 'No, Cancel',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showCloseButton: false,
    }); 
    
    if (result.isConfirmed) {
      const updatedServices = services.filter(
        service => !(service.servicesMasterId === servicesMasterId && service.servicesChildId === servicesChildId)
      );
      setValue('services', updatedServices, { shouldDirty: true });
      await trigger('services');
    }
  };

  const handleUpdateService = async (servicesMasterId, servicesChildId, updatedFields) => {
    const updatedServices = services.map(service => {
      if (service.servicesMasterId === servicesMasterId && service.servicesChildId === servicesChildId) {
        return { ...service, ...updatedFields };
      }
      return service;
    });
    setValue('services', updatedServices, { shouldDirty: true });
    await trigger('services');
  };

  // Handle brand-specific updates
  const handleBrandUpdate = async (serviceIndex, brandIndex, field, value) => {
    const updatedServices = [...services];
    
    // Ensure ServiceBrands array exists
    if (!updatedServices[serviceIndex].ServiceBrands) {
      updatedServices[serviceIndex].ServiceBrands = [];
    }
    
    // Ensure the specific brand object exists
    if (!updatedServices[serviceIndex].ServiceBrands[brandIndex]) {
      updatedServices[serviceIndex].ServiceBrands[brandIndex] = {
        serviceBrandId: 0,
        serviceBrandName: '',
        serviceTime: 0,
        price: 0
      };
    }
    
    // Update the specific field
    updatedServices[serviceIndex].ServiceBrands[brandIndex][field] = value;
    
    setValue('services', updatedServices, { shouldDirty: true });
    await trigger(`services.${serviceIndex}.ServiceBrands.${brandIndex}.${field}`);
  };

  // Replace the section in SelectedServices component from line ~120 onwards:

const isExistingService = (servicesMasterId, servicesChildId) => {
  return providerServiceList.some(
    service => service.servicesMasterId === servicesMasterId && service.servicesChildId === servicesChildId
  );
};

// Show ALL services in the form that have valid IDs - not just "new" ones
const allFormServices = services.filter(service => 
  service && service.servicesMasterId && service.servicesChildId
);

// Validate brand selection
const validateBrandSelection = (serviceIndex, selectedBrands) => {
  const brandFieldName = `services.${serviceIndex}.brands`;
  
  if (!selectedBrands || selectedBrands.length === 0) {
    setError(brandFieldName, {
      type: 'required',
      message: 'At least one variation must be selected'
    });
    return false;
  } else {
    clearErrors(brandFieldName);
    return true;
  }
};

const handleBrandSelection = async (serviceIndex, selectedBrands) => {
  // Validate brand selection
  const isValid = validateBrandSelection(serviceIndex, selectedBrands);
  
  const updatedServices = [...services];
  
  if (updatedServices[serviceIndex]) {
    const serviceBrands = selectedBrands.map(brand => ({
      serviceBrandId: brand.brandId,
      serviceBrandName: brand.brandDetail.title,
      serviceTime: 0,
      price: 0
    }));
    
    updatedServices[serviceIndex] = {
      ...updatedServices[serviceIndex],
      ServiceBrands: serviceBrands
    };
    
    setValue('services', updatedServices, { shouldDirty: true });
    await trigger('services');
    
    if (isValid) {
      await trigger(`services.${serviceIndex}.brands`);
    }
  }
};

const getSelectedBrands = (serviceIndex) => {
  const service = services[serviceIndex];
  if (!service || !service.ServiceBrands) return [];
  
  // Get available brands (including default if needed)
  const availableBrands = getBrandList(service);
  
  return service.ServiceBrands.map(serviceBrand => {
    return availableBrands.find(brand => brand.brandId === serviceBrand.serviceBrandId);
  }).filter(Boolean);
};

// Get the actual service index in the full services array
const getActualServiceIndex = (service) => {
  return services.findIndex(s => 
    s && s.servicesMasterId === service.servicesMasterId && 
    s.servicesChildId === service.servicesChildId
  );
};

// Check if brands are selected for a service
const hasBrandsSelected = (service) => {
  return service.ServiceBrands && service.ServiceBrands.length > 0;
};

// Check if only default brand is available
const hasOnlyDefaultBrand = (service) => {
  const availableBrands = getBrandList(service);
  return availableBrands.length === 1 && availableBrands[0].brandDetail.title === "General Brand";
};

// Auto-select default brands for services that need it
useEffect(() => {
  const autoSelectAllDefaultBrands = async () => {
    for (let i = 0; i < allFormServices.length; i++) {
      const service = allFormServices[i];
      const actualServiceIndex = getActualServiceIndex(service);
      const onlyDefaultBrand = hasOnlyDefaultBrand(service);
      
      if (onlyDefaultBrand && !hasBrandsSelected(service)) {
        await autoSelectDefaultBrand(actualServiceIndex, service);
      }
    }
  };

  if (allFormServices.length > 0) {
    autoSelectAllDefaultBrands();
  }
}, [allFormServices.length, JSON.stringify(allFormServices.map(s => ({ id: s.servicesMasterId, childId: s.servicesChildId })))]);

return (
  <Card>
    {allFormServices.map((service, displayIndex) => {
      const actualServiceIndex = getActualServiceIndex(service);
      const brandFieldName = `services.${actualServiceIndex}.brands`;
      const brandError = errors?.services?.[actualServiceIndex]?.brands;
      const availableBrands = getBrandList(service);
      const onlyDefaultBrand = hasOnlyDefaultBrand(service);
      
      return (
        <Grid container key={`${service.servicesMasterId}-${service.servicesChildId}`} sx={{borderBottom:'1px solid #ccc',padding:'0.5rem'}} spacing={2}>
          <Grid item xs={1.5}>
            <Typography sx={{fontWeight:'600',fontSize:'0.75rem'}}>Category</Typography>
            <Typography sx={{fontSize:'0.75rem'}}>{service.subCategoryTitle}</Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography sx={{fontWeight:'600',fontSize:'0.75rem'}}>Master Service</Typography>
            <Typography sx={{fontSize:'0.75rem'}}>{service.masterServiceTitle}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography sx={{fontWeight:'600',fontSize:'0.75rem'}}>Child Service</Typography>
            <Typography sx={{fontSize:'0.75rem'}}>{service.childServiceTitle}</Typography>
          </Grid>
          <Grid item xs={5}>
            {onlyDefaultBrand ? (
              // Show disabled field for default brand only
              <TextField
                size='small'
                variant="outlined"
                label="Selected Brands"
                value="General Brand"
                disabled
                sx={{
                  width: '100%',
                  '& .MuiInputBase-input': {
                    fontSize: '0.75rem',
                  },
                }}
              />
            ) : (
              // Show normal autocomplete for multiple brands
              <Autocomplete
                sx={{
                  '& .MuiAutocomplete-input': {
                    fontSize: '0.75rem',
                  },
                  '& .MuiOutlinedInput-root': {
                    borderColor: brandError ? '#d32f2f' : undefined,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: brandError ? '#d32f2f' : undefined,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: brandError ? '#d32f2f' : undefined,
                    },
                  },
                }}
                multiple
                disableCloseOnSelect
                options={availableBrands}
                getOptionLabel={(option) => option.brandDetail.title}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ 
                        marginRight: 8,
                        transform: 'scale(0.8)',
                      }}
                      checked={selected}
                    />
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {option.brandDetail.title}
                    </Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size='small'
                    variant="outlined"
                    placeholder="Select Variation"
                    label="Selected Variations"
                    error={!!brandError}
                    {...register(brandFieldName, {
                      validate: (value) => {
                        const selectedBrands = getSelectedBrands(actualServiceIndex);
                        return selectedBrands.length > 0 || 'At least one variation must be selected';
                      }
                    })}
                  />
                )}
                onChange={(_, newValue) => {
                  handleBrandSelection(actualServiceIndex, newValue);
                }}
                isOptionEqualToValue={(option, value) => option.brandId === value.brandId}
                value={getSelectedBrands(actualServiceIndex)}
                renderTags={(selected, getTagProps) =>
                  selected.map((option, index) => {
                  const { key, ...chipProps } = getTagProps({ index });
                  return (
                      <Chip
                      key={key}
                      label={option.brandDetail.title}
                      {...chipProps}
                      size="small"
                      sx={{
                          fontSize: "0.7rem",
                          px: .2,
                          py: 0.2,
                          "& .MuiChip-deleteIcon": {
                          color: "#bb3636",
                          fontSize: "16px"
                          }
                      }}
                      />
                  );
                  })
                }
              />
            )}
            {brandError && !onlyDefaultBrand && (
              <FormHelperText error sx={{ fontSize: '0.65rem', ml: 1, mt: 0.5 }}>
                {brandError.message}
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={0.5}>
            <IconButton
              onClick={() => handleRemoveService(service.servicesMasterId, service.servicesChildId)}
              size="small"
              color='error'
            >
              <DeleteIcon fontSize="small" color='error'/>
            </IconButton>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2} sx={{alignItems:"center"}}>
              <Grid item xs={3.5}>
                {hasBrandsSelected(service) ? (
                  <Typography sx={{fontWeight:'600',fontSize:'0.75rem',textAlign:'end'}}>Service Variations :</Typography>
                ) : (
                  <Typography sx={{fontWeight:'400',fontSize:'0.7rem',textAlign:'end',color:'text.secondary',fontStyle:'italic'}}>
                    {onlyDefaultBrand ? 'Default brand auto-selected' : 'Select brands to continue'}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={8.5}>
                
                <Card sx={{bgcolor:'rgba(0,0,0,0.05)'}}>
                {hasBrandsSelected(service) ? (
                  <Grid item xs={8.5}>
                  {service?.ServiceBrands && service.ServiceBrands.length > 0 && service.ServiceBrands.map((brand, brandIndex) => (
                    <Grid container spacing={2} key={`brand-${brandIndex}`} sx={{borderBottom:service.ServiceBrands.length - 1 === brandIndex ? 'none' : '1px solid #ccc',padding:'0.5rem' ,alignItems:"center"}}>
                      <Grid item xs={5}>
                        <Typography sx={{ fontSize: '0.75rem',fontWeight:'600',textAlign:'center' }}>{brand.serviceBrandName}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          {...register(`services.${actualServiceIndex}.ServiceBrands.${brandIndex}.serviceTime`, {
                            required: "Time is required",
                            min: { value: 1, message: "Time must be greater than 0" }
                          })}
                          type="number"
                          placeholder="0"
                          label="Time*"
                          value={formatValue(brand.serviceTime)}
                          onChange={(e) => {
                              const inputValue = e.target.value;
                              let finalValue;
                              if (inputValue === '' || /^\d*\.?\d*$/.test(inputValue)) {
                              finalValue = formatValue(inputValue);

                              if(finalValue==''){
                                  finalValue=0
                              }
                              handleBrandUpdate(actualServiceIndex, brandIndex, 'serviceTime', parseInt(finalValue));
                            }
                          }}
                          size="small"
                          inputMode="numeric"
                          error={!!errors?.services?.[actualServiceIndex]?.ServiceBrands?.[brandIndex]?.serviceTime}
                          inputProps={{
                            style: { textAlign: 'center', height: '1rem', fontSize: '0.75rem' },
                            min: 0,
                            onFocus: (e) => e.target.select(),
                            onClick: (e) => e.target.select(),
                            onWheel: (e) => e.target.blur(),
                            onKeyDown: (e) => {
                              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                e.preventDefault();
                              }
                            }
                            
                          }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">mins.</InputAdornment>
                          }}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          {...register(`services.${actualServiceIndex}.ServiceBrands.${brandIndex}.price`, {
                            required: "Price is required",
                            min: { value: 1, message: "Price must be greater than 0" }
                          })}
                          type="number"
                          placeholder="0"
                          label="Price*"
                          value={formatValue(brand.price)}
                          onChange={(e) => {
                            const value = formatValue(e.target.value);
                            if (!isNaN(value) && value >= 1) {
                              handleBrandUpdate(actualServiceIndex, brandIndex, 'price', value);
                            } else if (!isNaN(value) && value > 0 && value < 1) {
                              handleBrandUpdate(actualServiceIndex, brandIndex, 'price', 1.00);
                            } else if (e.target.value === '') {
                              handleBrandUpdate(actualServiceIndex, brandIndex, 'price', 0);
                            }
                          }}
                          inputMode="numeric"
                          size="small"
                          error={!!errors?.services?.[actualServiceIndex]?.ServiceBrands?.[brandIndex]?.price}
                          inputProps={{
                            style: { textAlign: 'center', height: '1rem', fontSize: '0.75rem' },
                            min: 0,
                            step: 0.01,
                            onFocus: (e) => e.target.select(),
                            onClick: (e) => e.target.select(),
                            onWheel: (e) => e.target.blur(),
                            onKeyDown: (e) => {
                              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                e.preventDefault();
                              }
                            }
                          }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>
                          }}
                        />
                      </Grid>
                    </Grid>
                  ))}
                
              </Grid>
                ) : (
                  <Card sx={{
                    bgcolor: onlyDefaultBrand ? 'rgba(76,175,80,0.1)' : 'rgba(255,193,7,0.1)',
                    border: onlyDefaultBrand ? '1px dashed #4caf50' : '1px dashed #ffc107',
                    padding:'1rem',
                    textAlign:'center'
                  }}>
                    <Typography sx={{
                      fontSize:'0.7rem',
                      color: onlyDefaultBrand ? '#2e7d32' : '#ff8f00',
                      fontStyle:'italic'
                    }}>
                      {onlyDefaultBrand 
                        ? 'Default brand will be automatically selected'
                        : 'Please select at least one variation to configure time and price'
                      }
                    </Typography>
                  </Card>
                )}
                
              </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    })}
  </Card>
);
}

export default SelectedServices;